/**
 * BulkUploadCard — multi-file profile document upload.
 *
 * Flow: drag-drop (or browse) several PDFs → each lands as a row with a
 * doc-type dropdown (guessed from the filename, override per row) → "Upload
 * all" runs them sequentially, each row showing queued → uploading → done /
 * failed. A failure on one row doesn't block the rest.
 *
 * Reuses the single-file endpoint (uploadProfileDocument) in a loop — no
 * backend bulk endpoint needed.
 */
import * as React from 'react';
import {
  Alert,
  AlertDescription,
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Spinner,
  useToast,
} from '@finmin/ui';
import {
  AlertCircle,
  CheckCircle2,
  FileText,
  FileUp,
  Trash2,
  Upload,
  XCircle,
} from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import {
  MAX_PROFILE_UPLOAD_BYTES,
  profileDocumentKeys,
  uploadProfileDocument,
  useSupportedProfileDocumentTypes,
} from '@/api/profile-documents';
import { formatDocumentType } from './format';

type RowStatus = 'queued' | 'uploading' | 'done' | 'failed';

interface Row {
  id: string;
  file: File;
  docType: string;
  status: RowStatus;
  progress: number;
  error?: string;
}

function guessDocType(filename: string, supported: string[]): string {
  const n = filename.toLowerCase();
  const rules: Array<[RegExp, string]> = [
    [/\b(term|life)\b/, 'term_insurance'],
    [/\b(health|mediclaim|medical)\b/, 'health_insurance'],
    [/\b(nps|pran|pension)\b/, 'nps'],
    [/\b(home|housing|sanction|loan)\b/, 'housing_loan'],
    [/\b(ecas|cas|portfolio|statement)\b/, 'ecas'],
  ];
  for (const [re, type] of rules) {
    if (re.test(n) && supported.includes(type)) return type;
  }
  return supported[0] ?? '';
}

function uid(): string {
  return Math.random().toString(36).slice(2, 10);
}

export function BulkUploadCard() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const supported = useSupportedProfileDocumentTypes();
  const docTypes = supported.data?.document_types ?? [];

  const [rows, setRows] = React.useState<Row[]>([]);
  const [isDragging, setIsDragging] = React.useState(false);
  const [isUploading, setIsUploading] = React.useState(false);

  function addFiles(fileList: FileList | File[]) {
    const incoming = Array.from(fileList);
    setRows((prev) => {
      const existing = new Set(prev.map((r) => `${r.file.name}:${r.file.size}`));
      const next: Row[] = [...prev];
      for (const file of incoming) {
        const key = `${file.name}:${file.size}`;
        if (existing.has(key)) continue;
        if (file.size > MAX_PROFILE_UPLOAD_BYTES) {
          toast({
            title: 'File too large',
            description: `${file.name} exceeds 20 MB.`,
            variant: 'error',
          });
          continue;
        }
        existing.add(key);
        next.push({
          id: uid(),
          file,
          docType: guessDocType(file.name, docTypes),
          status: 'queued',
          progress: 0,
        });
      }
      return next;
    });
  }

  function handleDrop(e: React.DragEvent) {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files?.length) addFiles(e.dataTransfer.files);
  }

  function setRow(id: string, patch: Partial<Row>) {
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  }

  function removeRow(id: string) {
    setRows((prev) => prev.filter((r) => r.id !== id));
  }

  async function uploadAll() {
    const pending = rows.filter(
      (r) => r.status === 'queued' || r.status === 'failed',
    );
    if (pending.length === 0) return;
    setIsUploading(true);

    let ok = 0;
    let failed = 0;
    // Sequential: one row at a time so a slow/large file doesn't stall others
    // and we stay gentle on S3.
    for (const row of pending) {
      if (!row.docType) {
        setRow(row.id, { status: 'failed', error: 'Pick a document type' });
        failed += 1;
        continue;
      }
      setRow(row.id, { status: 'uploading', progress: 0, error: undefined });
      try {
        await uploadProfileDocument(row.file, row.docType, {
          onProgress: (p) => setRow(row.id, { progress: p }),
        });
        setRow(row.id, { status: 'done', progress: 100 });
        ok += 1;
      } catch (err) {
        setRow(row.id, {
          status: 'failed',
          error: err instanceof Error ? err.message : 'Upload failed',
        });
        failed += 1;
      }
    }

    setIsUploading(false);
    await queryClient.invalidateQueries({ queryKey: profileDocumentKeys.list() });
    await queryClient.invalidateQueries({
      queryKey: profileDocumentKeys.extractions(),
    });

    if (ok > 0) {
      toast({
        title: `Uploaded ${ok} document${ok > 1 ? 's' : ''}`,
        description:
          failed > 0
            ? `${failed} failed — retry from the list below.`
            : "We'll process them shortly.",
        variant: failed > 0 ? 'warning' : 'success',
      });
    } else if (failed > 0) {
      toast({
        title: 'Upload failed',
        description: 'None of the files uploaded. Check the errors and retry.',
        variant: 'error',
      });
    }
  }

  const pendingCount = rows.filter(
    (r) => r.status === 'queued' || r.status === 'failed',
  ).length;
  const doneCount = rows.filter((r) => r.status === 'done').length;

  return (
    <Card>
      <CardHeader className="pb-4">
        <CardTitle className="text-base flex items-center gap-2">
          <FileUp className="h-4 w-4 text-muted-foreground" aria-hidden />
          Upload documents
        </CardTitle>
        <CardDescription>
          Drop one or more PDFs, set the type for each, then upload them all at
          once. Up to 20 MB per file.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {supported.error ? (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              Couldn't load supported document types. Try again later.
            </AlertDescription>
          </Alert>
        ) : (
          <>
            {/* Drop zone */}
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              onDragOver={(e) => {
                e.preventDefault();
                setIsDragging(true);
              }}
              onDragLeave={() => setIsDragging(false)}
              onDrop={handleDrop}
              className={[
                'w-full rounded-lg border-2 border-dashed p-8 text-center transition-colors',
                isDragging
                  ? 'border-primary bg-primary/5'
                  : 'border-border hover:border-foreground/30 hover:bg-secondary/30',
              ].join(' ')}
            >
              <FileUp
                className="mx-auto h-8 w-8 text-muted-foreground/60"
                aria-hidden
              />
              <p className="mt-2 text-sm font-medium text-foreground">
                Drag &amp; drop PDFs here
              </p>
              <p className="mt-0.5 text-xs text-muted-foreground">
                or click to browse
              </p>
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf"
              multiple
              className="hidden"
              onChange={(e) => {
                if (e.target.files?.length) addFiles(e.target.files);
                e.target.value = '';
              }}
            />

            {/* Staged file list */}
            {rows.length > 0 && (
              <ul className="space-y-2">
                {rows.map((row) => (
                  <li
                    key={row.id}
                    className="flex items-center gap-3 rounded-md border border-border px-3 py-2"
                  >
                    <FileText
                      className="h-4 w-4 text-muted-foreground shrink-0"
                      aria-hidden
                    />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-foreground truncate">
                        {row.file.name}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {(row.file.size / 1024 / 1024).toFixed(2)} MB
                        {row.status === 'uploading' && row.progress > 0
                          ? ` · ${row.progress}%`
                          : ''}
                        {row.error ? ` · ${row.error}` : ''}
                      </p>
                    </div>

                    {/* Per-file doc type */}
                    <select
                      value={row.docType}
                      onChange={(e) => setRow(row.id, { docType: e.target.value })}
                      disabled={row.status === 'uploading' || row.status === 'done'}
                      className="h-8 rounded-md border border-input bg-background px-2 text-xs text-foreground focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50"
                    >
                      {docTypes.map((t) => (
                        <option key={t} value={t}>
                          {formatDocumentType(t)}
                        </option>
                      ))}
                    </select>

                    {/* Status / remove */}
                    <div className="w-6 flex justify-center shrink-0">
                      <RowStatusIcon status={row.status} />
                    </div>
                    {row.status !== 'uploading' && row.status !== 'done' && (
                      <button
                        type="button"
                        onClick={() => removeRow(row.id)}
                        className="text-muted-foreground hover:text-destructive transition-colors shrink-0"
                        aria-label={`Remove ${row.file.name}`}
                      >
                        <Trash2 className="h-3.5 w-3.5" aria-hidden />
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            )}

            {/* Actions */}
            {rows.length > 0 && (
              <div className="flex items-center justify-between gap-3">
                <p className="text-xs text-muted-foreground">
                  {doneCount > 0 && `${doneCount} uploaded · `}
                  {pendingCount} pending
                </p>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setRows([])}
                    disabled={isUploading}
                  >
                    Clear
                  </Button>
                  <Button
                    size="sm"
                    onClick={uploadAll}
                    disabled={isUploading || pendingCount === 0}
                    className="gap-1.5"
                  >
                    {isUploading ? (
                      <>
                        <Spinner className="h-3.5 w-3.5" />
                        Uploading…
                      </>
                    ) : (
                      <>
                        <Upload className="h-3.5 w-3.5" aria-hidden />
                        Upload {pendingCount > 0 ? pendingCount : ''}
                      </>
                    )}
                  </Button>
                </div>
              </div>
            )}

            <p className="text-xs text-muted-foreground">
              We'll never share these documents — only your assigned advisor and
              our admin team can access them.
            </p>
          </>
        )}
      </CardContent>
    </Card>
  );
}

function RowStatusIcon({ status }: { status: RowStatus }) {
  switch (status) {
    case 'uploading':
      return <Spinner className="h-4 w-4" />;
    case 'done':
      return <CheckCircle2 className="h-4 w-4 text-success" aria-label="Uploaded" />;
    case 'failed':
      return <XCircle className="h-4 w-4 text-destructive" aria-label="Failed" />;
    default:
      return (
        <Badge variant="outline" className="h-5 px-1.5 text-[10px]">
          queued
        </Badge>
      );
  }
}
